import { useMutation, useQuery, useSubscription } from '@apollo/client';
import * as _ from 'lodash-es';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import isNotDetached from './isNotDetached';
import useExternalPostPlatforms from './useExternalPostPlatforms';
import useAppSelector from '../app/redux/useAppSelector';
import type { DocumentType } from '../graphql/generated';
import { gql } from '../graphql/generated';
import { UserType } from '../graphql/generated/graphql';
import { filterTreeListTopics } from '../topic/toTopicTreeList';
import type { AllTopicsTreeListTopic } from '../topic/useAllTopics';
import useAllTopics from '../topic/useAllTopics';
import useMembershipTopics from '../topic/useMembershipTopics';

export const getSidebarSectionNameWithFallback = (name?: string) =>
  name?.trim() || 'Section';

const SECTION_FRAGMENT = gql(`
  fragment SidebarSectionsProvider_section on Section {
    id
    name
    icon
    color
    items {
      ... on Post {
        id
      }
      ... on Topic {
        id
      }
    }
  }
`);

type CreateAction = 'create';
type DeleteAction = 'delete';
type SoftAction = 'update' | 'move' | 'hide' | 'show';

type CreateActionSequence = [CreateAction, ...SoftAction[]];
type DeleteActionSequence = [...SoftAction[], DeleteAction];
type ActionSequence =
  | [SoftAction, ...SoftAction[]]
  | DeleteActionSequence
  | CreateActionSequence;

function hasCreateAction(
  actions: ActionSequence | undefined,
): actions is CreateActionSequence {
  return !!actions && _.first(actions) === 'create';
}

function hasDeleteAction(
  actions: ActionSequence | undefined,
): actions is DeleteActionSequence {
  return !!actions && _.last(actions) === 'delete';
}

export function isSectionNew(section: Section) {
  return hasCreateAction(section.actions);
}

export function isSectionDeleted(section: Section) {
  return hasDeleteAction(section.actions);
}

export type Section = DocumentType<typeof SECTION_FRAGMENT> & {
  actions?: ActionSequence;
  premade?: boolean;
  visible: boolean;
  isTeamSection: boolean;
};

export const PremadeSections = {
  MY_TOPICS: 'my_topics',
  ALL_TOPICS: 'all_topics',
  INTEGRATIONS: 'integrations',
} as const;

type PremadeSectionId = (typeof PremadeSections)[keyof typeof PremadeSections];

export const isPremadeSectionId = (id: string): id is PremadeSectionId =>
  Object.values(PremadeSections).includes(id as PremadeSectionId);

export const canEditSection = (
  id: string,
  options: {
    isTeamSection: boolean;
    isAdminUser: boolean;
  },
) =>
  !Object.values(PremadeSections).includes(id as PremadeSectionId) &&
  (options.isAdminUser || !options.isTeamSection);

const SIDEBAR_SECTIONS_MODAL_QUERY = gql(/** @complexity 20 */ `
  query SidebarSectionsProvider {
    currentUser {
      sections {
        id
        ...SidebarSectionsProvider_section
      }
    } 
    currentSession {
      user {
        id
        membershipTopics(includeGroups: true) {
          id
        }
        settings {
          sidebar {
            myTopicIds
            sectionIds
          }
        }
      }
      organization {
        id
        sections {
          id
          ...SidebarSectionsProvider_section
        }
      }
    }
  }
`);

const SECTION_UPDATED_SUBSCRIPTION = gql(/** @complexity 12 */ `
  subscription SectionUpdated {
    sectionUpdated {
      id
      ...SidebarSectionsProvider_section
    }
  }
`);

const USER_SETTINGS_UPDATED_SUBSCRIPTION = gql(/** @complexity 8 */ `
  subscription UserSettingsUpdated {
    userSettingsUpdated {
      sidebar {
        sectionIds
      }
    }
  }
`);

const SECTION_DELETED_SUBSCRIPTION = gql(/** @complexity 4 */ `
  subscription SectionDeleted {
    sectionDeleted {
      id
    }
  }
`);

const UPDATE_SIDEBAR_SECTION_IDS = gql(/** @complexity 4 */ `
  mutation updateSidebarSectionIds($sectionIds: [ID!]!) {
    updateUserSettings(sidebar: {sectionIds: $sectionIds}) {
      sidebar {
        sectionIds
      }
    }
  }
`);

const CREATE_SIDEBAR_SECTION = gql(/** @complexity 6 */ `
  mutation createSidebarSection($name: String!, $color: String, $icon: String, $isTeamSection: Boolean, $itemIds: [ID!]) {
    createSection(name: $name, color: $color, icon: $icon, isTeamSection: $isTeamSection, itemIds: $itemIds) {
      ...SidebarSectionsProvider_section
    }
  }
`);

const DELETE_SIDEBAR_SECTION = gql(/** @complexity 2 */ `
  mutation deleteSidebarSection($id: ID!) {
    deleteSection(id: $id) {
      id
    }
  }
`);

const UPDATE_SIDEBAR_SECTION = gql(/** @complexity 6 */ `
  mutation updateSidebarSection($id: ID!, $name: String, $color: String, $icon: String, $isTeamSection: Boolean, $itemIds: [ID!]) {
    updateSection(id: $id, name: $name, color: $color, icon: $icon, isTeamSection: $isTeamSection, itemIds: $itemIds) {
      ...SidebarSectionsProvider_section
    }
  }
`);

const REORDER_ALL_TOPICS_MUTATION = gql(/** @complexity 4 */ `
  mutation reorderAllTopics($topicIds: [ID!]!) {
    reorderTopics(topicIds: $topicIds) {
      id
      order
    }
  }
`);

export const useUpdateSidebarSection = () =>
  useMutation(UPDATE_SIDEBAR_SECTION);

export const useUpdateSidebarSectionIds = () =>
  useMutation(UPDATE_SIDEBAR_SECTION_IDS);

const UPDATE_MY_TOPIC_IDS_ORDER = gql(/** @complexity 4 */ `
  mutation UpdateMyTopicIdsOrder($topicIds: [ID!]!) {
    updateUserSettings(sidebar: { myTopicIds: $topicIds }) {
      sidebar {
        myTopicIds
      }
    }
  }
`);

type SidebarSectionsContextType = {
  allSections: Section[];
  sidebarSections: Section[];
  unsavedSections: Section[];
  allTopics: AllTopicsTreeListTopic[];
  myTopics: AllTopicsTreeListTopic[];
  toggleSectionVisibility: (id: string) => void;
  moveSection: (fromIndex: number, toIndex: number) => void;
  deleteSection(id: string): void;
  restoreSection(id: string): void;
  addSection: (
    sectionData: Pick<Section, 'name'> &
      Partial<
        Pick<Section, 'items' | 'name' | 'icon' | 'color' | 'isTeamSection'>
      >,
  ) => Section;
  updateSection: (
    data: Pick<Section, 'id'> &
      Partial<Pick<Section, 'name' | 'icon' | 'color' | 'isTeamSection'>>,
  ) => void;
  addItemToSection: (id: string, item: Section['items'][number]) => void;
  removeItemFromSection: (id: string, itemId: string) => void;
  moveItemInSection: (id: string, fromIndex: number, toIndex: number) => void;
  saveSections: () => Promise<void>;
  permissions: {
    canSeeMyTopics: boolean;
    canSeeTeamSections: boolean;
    canEditTeamSections: boolean;
    canHideSections: boolean;
  };
};

const SidebarSectionsContext =
  React.createContext<SidebarSectionsContextType | null>(null);

export const useSidebarSectionsContext = () => {
  const context = useContext(SidebarSectionsContext);
  if (context === null) {
    throw new Error(
      'useSidebarSectionsContext must be used within SidebarSectionsContext',
    );
  }
  return context;
};

const moveItem = <T,>({
  items,
  from,
  to,
  transform,
}: {
  items: T[];
  from: number;
  to: number;
  transform?: (item: T) => T;
}): T[] => {
  const item = items[from];
  if (!item) return items;
  const clonedItems = items.slice();
  clonedItems.splice(from, 1);
  clonedItems.splice(to, 0, transform ? transform(item) : item);
  return clonedItems;
};

const usePremadeSections = (
  permissions: SidebarSectionsContextType['permissions'],
) => {
  const isGuest = useAppSelector(
    (state) => state.session.user.type === UserType.Guest,
  );
  const platforms = useExternalPostPlatforms();
  return useMemo(() => {
    return _.compact<Section & { id: PremadeSectionId }>([
      permissions.canSeeMyTopics && {
        id: PremadeSections.MY_TOPICS,
        name: isGuest ? 'Topics' : 'My Topics',
        items: [],
        premade: true,
        visible: true,
        isTeamSection: false,
      },
      permissions.canSeeTeamSections && {
        id: PremadeSections.ALL_TOPICS,
        name: 'All Topics',
        items: [],
        premade: true,
        visible: true,
        isTeamSection: true,
      },
      permissions.canSeeTeamSections &&
        platforms.length > 0 && {
          id: PremadeSections.INTEGRATIONS,
          name: 'Integrations',
          items: [],
          premade: true,
          visible: true,
          isTeamSection: true,
        },
    ]);
  }, [
    permissions.canSeeMyTopics,
    isGuest,
    permissions.canSeeTeamSections,
    platforms.length,
  ]);
};

interface SidebarSectionsProviderProps {
  children: React.ReactNode;
}

const SidebarSectionsProvider = ({
  children,
}: SidebarSectionsProviderProps) => {
  const userType = useAppSelector((state) => state.session.user.type);
  const permissions: SidebarSectionsContextType['permissions'] = useMemo(
    () => ({
      canSeeMyTopics: [UserType.Guest, UserType.User, UserType.Admin].includes(
        userType,
      ),
      canSeeTeamSections: [UserType.User, UserType.Admin].includes(userType),
      canEditTeamSections: userType === UserType.Admin,
      canHideSections: [UserType.User, UserType.Admin].includes(userType),
    }),
    [userType],
  );

  const { data, refetch, subscribeToMore } = useQuery(
    SIDEBAR_SECTIONS_MODAL_QUERY,
  );

  useSubscription(USER_SETTINGS_UPDATED_SUBSCRIPTION, {
    onData() {
      refetch();
    },
  });

  useEffect(() => {
    subscribeToMore({ document: SECTION_UPDATED_SUBSCRIPTION });
    subscribeToMore({
      document: SECTION_DELETED_SUBSCRIPTION,
      updateQuery(prev, { subscriptionData }) {
        return {
          ...prev,
          currentUser: {
            ...prev.currentUser,
            sections: prev.currentUser.sections.filter(
              (section) =>
                section.id !== subscriptionData.data.sectionDeleted.id,
            ),
          },
          currentSession: {
            ...prev.currentSession,
            user: {
              ...prev.currentSession.user,
              settings: {
                ...prev.currentSession.user.settings,
                sidebar: prev.currentSession.user.settings.sidebar && {
                  ...prev.currentSession.user.settings.sidebar,
                  sectionIds:
                    prev.currentSession.user.settings.sidebar.sectionIds.filter(
                      (id) => id !== subscriptionData.data.sectionDeleted.id,
                    ),
                },
              },
            },
          },
        };
      },
    });
  }, [subscribeToMore]);

  const [updateSidebarSectionIds] = useMutation(UPDATE_SIDEBAR_SECTION_IDS);
  const [createSidebarSection] = useMutation(CREATE_SIDEBAR_SECTION);
  const [deleteSidebarSection] = useMutation(DELETE_SIDEBAR_SECTION);
  const [updateSidebarSection] = useUpdateSidebarSection();
  const [updateMyTopicIdsOrder] = useMutation(UPDATE_MY_TOPIC_IDS_ORDER);
  const [reorderAllTopics] = useMutation(REORDER_ALL_TOPICS_MUTATION);

  const selectedSectionId = useAppSelector(
    (state) => state.ui.selectedSectionId,
  );

  const isModalOpen = selectedSectionId != null;

  const myTopicIds = useMemo(
    () => data?.currentSession.user.settings.sidebar?.myTopicIds ?? [],
    [data],
  );

  const treeTopics = useAllTopics();
  const filteredAllTopics = useMemo(
    () => filterTreeListTopics(treeTopics, isNotDetached),
    [treeTopics],
  );

  const flattenTopics = useAllTopics({ flatten: true });
  const directMembershipTopics = useMemo(
    () => data?.currentSession.user.membershipTopics ?? [],
    [data],
  );

  const myTopics = useMembershipTopics(flattenTopics, directMembershipTopics);
  const filteredMyTopics = useMemo(
    () =>
      myTopics
        ? _.sortBy(filterTreeListTopics(myTopics, isNotDetached), (topic) =>
            myTopicIds.indexOf(topic.id),
          )
        : [],
    [myTopics, myTopicIds],
  );
  const [localMyTopics, setLocalMyTopics] = useState(filteredMyTopics);
  useEffect(() => {
    setLocalMyTopics(filteredMyTopics);
  }, [filteredMyTopics, isModalOpen]);

  const [localAllTopics, setLocalAllTopics] = useState(filteredAllTopics);
  useEffect(() => {
    setLocalAllTopics(filteredAllTopics);
  }, [filteredAllTopics, isModalOpen]);

  const customPersonalSections = useMemo(
    () =>
      data?.currentUser.sections?.map((section) => ({
        ...section,
        isTeamSection: false,
      })) ?? [],
    [data?.currentUser.sections],
  );
  const customTeamSections = useMemo(
    () =>
      data?.currentSession.organization.sections?.map((section) => ({
        ...section,
        isTeamSection: true,
      })) ?? [],
    [data?.currentSession.organization.sections],
  );
  const premadeSections = usePremadeSections(permissions);

  const visibleSectionsIds = useMemo(
    () => data?.currentSession.user.settings.sidebar?.sectionIds ?? [],
    [data?.currentSession.user.settings.sidebar?.sectionIds],
  );

  const sections = useMemo<Section[]>(() => {
    const availableSections: Section[] = _.compact([
      ...customPersonalSections,
      ...customTeamSections,
      ...premadeSections,
    ]).map((section) => ({
      ...section,
      visible: visibleSectionsIds.includes(section.id),
    }));

    return _.sortBy(availableSections, [
      (section) => {
        const index = visibleSectionsIds.indexOf(section.id);
        return index === -1 ? Infinity : index;
      },
      (section) => (section.isTeamSection ? 1 : 0),
      (section) => (section.premade ? Infinity : section.name.toLowerCase()),
    ]);
  }, [
    customPersonalSections,
    customTeamSections,
    premadeSections,
    visibleSectionsIds,
  ]);

  const [localSections, setLocalSections] = useState<Section[]>(sections);

  useEffect(() => {
    setLocalSections(sections);
  }, [sections, isModalOpen]);

  const sidebarSectionsContextValue =
    useMemo((): SidebarSectionsContextType => {
      const allSections = localSections;

      const localSidebarSections = allSections.filter(
        (section) =>
          (section.visible || !permissions.canHideSections) &&
          !hasDeleteAction(section.actions),
      );

      return {
        permissions,
        allTopics: localAllTopics,
        myTopics: localMyTopics,
        allSections,
        sidebarSections: localSidebarSections,
        unsavedSections: localSections.filter((section) => {
          if (section.actions && section.actions?.length > 0) {
            // moving an invisible section is not considered an unsaved change
            // we will still generate the 'move' action for it since it could be that
            // the user will make it visible afterwards
            const isInvisibleMove =
              !section.visible &&
              section.actions.every((action) => action === 'move');

            return !isInvisibleMove;
          }

          return false;
        }),
        moveSection(from, to) {
          setLocalSections((currentSections) => {
            return moveItem({
              items: currentSections,
              from,
              to,
              transform: (section) => {
                if (hasDeleteAction(section.actions)) {
                  return section;
                }

                return {
                  ...section,
                  actions: [...(section.actions ?? []), 'move'],
                };
              },
            });
          });
        },
        addSection(sectionData) {
          const newSection: Section = {
            ...sectionData,
            items: sectionData.items ?? [],
            id: _.uniqueId('create'),
            actions: ['create'],
            visible: true,
            isTeamSection: sectionData.isTeamSection ?? false,
          };

          setLocalSections((currentSections) => {
            const firstPremadeSectionIndex = Math.max(
              currentSections.findIndex(({ id }) => isPremadeSectionId(id)),
              0,
            );

            return [
              ...currentSections.slice(0, firstPremadeSectionIndex),
              newSection,
              ...currentSections.slice(firstPremadeSectionIndex),
            ];
          });

          return newSection;
        },
        deleteSection(id) {
          setLocalSections((currentSections) => {
            const newSections = currentSections.map(
              (section): Section | null => {
                if (section.id !== id || hasDeleteAction(section.actions)) {
                  return section;
                }

                if (hasCreateAction(section.actions)) {
                  return null;
                }

                return {
                  ...section,
                  actions: [...(section.actions ?? []), 'delete'],
                };
              },
            );

            return _.compact(newSections);
          });
        },
        restoreSection(id) {
          setLocalSections((currentSections) =>
            currentSections.map((section): Section => {
              if (section.id !== id || !hasDeleteAction(section.actions)) {
                return section;
              }

              const actions = section.actions.filter(
                (action) => action !== 'delete',
              ) as typeof section.actions;

              return {
                ...section,
                actions: actions.length ? actions : undefined,
              };
            }),
          );
        },
        addItemToSection(id, item) {
          setLocalSections((currentSections) =>
            currentSections.map((section) => {
              if (section.id === id && !hasDeleteAction(section.actions)) {
                return {
                  ...section,
                  items: [...section.items, item],
                  actions: [...(section.actions ?? []), 'update'],
                };
              }

              return section;
            }),
          );
        },
        removeItemFromSection(id, itemId) {
          setLocalSections((currentSections) =>
            currentSections.map((section) => {
              if (section.id === id && !hasDeleteAction(section.actions)) {
                return {
                  ...section,
                  items: section.items.filter((item) => item.id !== itemId),
                  actions: [...(section.actions ?? []), 'update'],
                };
              }

              return section;
            }),
          );
        },
        moveItemInSection(id, fromIndex, toIndex) {
          const markPremadeSectionAsUpdated = () => {
            setLocalSections((currentSections) =>
              currentSections.map((section) =>
                section.id === id
                  ? { ...section, actions: ['update'] } // Premade sections can only ever be updated
                  : section,
              ),
            );
          };

          if (id === PremadeSections.MY_TOPICS) {
            setLocalMyTopics((current) =>
              moveItem({ items: current, from: fromIndex, to: toIndex }),
            );

            markPremadeSectionAsUpdated();
          } else if (id === PremadeSections.ALL_TOPICS) {
            setLocalAllTopics((current) =>
              moveItem({ items: current, from: fromIndex, to: toIndex }),
            );

            markPremadeSectionAsUpdated();
          } else {
            setLocalSections((currentSections) =>
              currentSections.map((section) => {
                if (section.id === id && !hasDeleteAction(section.actions)) {
                  return {
                    ...section,
                    items: moveItem({
                      items: section.items,
                      from: fromIndex,
                      to: toIndex,
                    }),
                    actions: [...(section.actions ?? []), 'update'],
                  };
                }

                return section;
              }),
            );
          }
        },

        updateSection({ id, ...sectionData }) {
          setLocalSections((currentSections) =>
            currentSections.map((section) => {
              if (section.id === id && !hasDeleteAction(section.actions)) {
                return {
                  ...section,
                  ..._.omitBy(sectionData, _.isNil),
                  actions: [...(section.actions ?? []), 'update'],
                };
              }

              return section;
            }),
          );
        },
        saveSections: async () => {
          const saveResults = await Promise.all(
            localSections.map(
              async (
                section,
              ): Promise<{ section: Section | null; error?: unknown }> => {
                const wasOriginallyVisible = visibleSectionsIds.includes(
                  section.id,
                );

                if (hasCreateAction(section.actions)) {
                  try {
                    const result = await createSidebarSection({
                      variables: {
                        name: getSidebarSectionNameWithFallback(section.name),
                        itemIds: section.items.map(({ id }) => id),
                        icon: section.icon,
                        color: section.color,
                        isTeamSection: section.isTeamSection,
                      },
                    });

                    if (!result.data) throw new Error('Section not created');

                    return {
                      section: {
                        ...result.data.createSection,
                        visible: section.visible,
                        isTeamSection: section.isTeamSection,
                      },
                    };
                  } catch (error) {
                    // Section couldn't be created, so we don't want to add it to the sidebar
                    return { section: null, error };
                  }
                }

                if (_.last(section.actions) === 'delete') {
                  try {
                    const result = await deleteSidebarSection({
                      variables: { id: section.id },
                    });
                    if (!result.data) throw new Error('Section not deleted');

                    // Section was deleted, so we don't store it in the sidebar
                    return { section: null };
                  } catch (error) {
                    return {
                      section: {
                        // Section couldn't be deleted, so we keep it as it was
                        ...section,
                        // in case it was originally visible in the sidebar, we keep it as such
                        visible: wasOriginallyVisible,
                      },
                      error,
                    };
                  }
                }

                if (section.actions?.includes('update')) {
                  try {
                    if (section.id === PremadeSections.MY_TOPICS) {
                      await updateMyTopicIdsOrder({
                        variables: {
                          topicIds: localMyTopics.map((topic) => topic.id),
                        },
                      });
                    } else if (section.id === PremadeSections.ALL_TOPICS) {
                      await reorderAllTopics({
                        variables: {
                          topicIds: localAllTopics.map((topic) => topic.id),
                        },
                      });
                    } else {
                      const result = await updateSidebarSection({
                        variables: {
                          id: section.id,
                          name: getSidebarSectionNameWithFallback(section.name),
                          itemIds: section.items.map((item) => item.id),
                          icon: section.icon,
                          color: section.color,
                          isTeamSection: section.isTeamSection,
                        },
                      });

                      if (result.data?.updateSection) {
                        return {
                          section: {
                            ...result.data.updateSection,
                            visible: section.visible,
                            isTeamSection: section.isTeamSection,
                          },
                        };
                      }
                      return { section };
                    }
                  } catch (error) {
                    return { section, error };
                  }
                }

                return { section };
              },
            ),
          );

          const sectionIds = _.compact(
            saveResults.map(({ section }) => section?.visible && section?.id),
          );

          if (!_.isEqual(sectionIds, visibleSectionsIds)) {
            await updateSidebarSectionIds({
              variables: {
                sectionIds,
              },
            });
          }
          await refetch();

          if (saveResults.some(({ error }) => !!error)) {
            throw new Error('Some save actions ended with an error');
          }
        },
        toggleSectionVisibility(id: string) {
          const wasOriginallyVisible = visibleSectionsIds.includes(id);

          setLocalSections((currentSections) => {
            return currentSections.map((section) => {
              if (section.id === id && !hasDeleteAction(section.actions)) {
                const { actions } = section;
                const nextActions = (() => {
                  if (actions) {
                    if (wasOriginallyVisible && actions.includes('hide')) {
                      return actions.filter(
                        (action) => action !== 'hide',
                      ) as typeof actions;
                    }

                    if (!wasOriginallyVisible && actions.includes('show')) {
                      return actions.filter(
                        (action) => action !== 'show',
                      ) as typeof actions;
                    }
                  }

                  return [
                    ...(actions ?? []),
                    section.visible ? 'hide' : 'show',
                  ] as CreateActionSequence;
                })();

                return {
                  ...section,
                  visible: !section.visible,
                  actions: nextActions,
                };
              }

              return section;
            });
          });
        },
      };
    }, [
      localSections,
      permissions,
      localMyTopics,
      localAllTopics,
      visibleSectionsIds,
      refetch,
      createSidebarSection,
      deleteSidebarSection,
      updateMyTopicIdsOrder,
      updateSidebarSection,
      updateSidebarSectionIds,
      reorderAllTopics,
    ]);

  return (
    <SidebarSectionsContext.Provider value={sidebarSectionsContextValue}>
      {children}
    </SidebarSectionsContext.Provider>
  );
};

export default SidebarSectionsProvider;
